import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSpinner, FaCity, FaMapMarker, FaSort, FaStar, FaChevronLeft, FaChevronRight, FaGlobe } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

const CityManagement = () => {
    const [cities, setCities] = useState([]);
    const [states, setStates] = useState([]);
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingCity, setEditingCity] = useState(null);
    const [filterCountry, setFilterCountry] = useState('');
    const [filterState, setFilterState] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        state_id: '',
        sort_order: 0,
        lang: 'en',
        is_default: 0
    });
    
    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(15);
    const [totalItems, setTotalItems] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [tempSearch, setTempSearch] = useState('');

    useEffect(() => {
        fetchCountries();
        fetchCities();
    }, []);

    useEffect(() => {
        if (filterCountry) {
            fetchStatesByCountry();
        } else {
            setStates([]);
        }
    }, [filterCountry]);

    useEffect(() => {
        fetchCities();
    }, [filterCountry, filterState, searchTerm, currentPage]);

    const fetchCountries = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get('https://app.bihebari.com.np/api/locations/countries', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setCountries(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching countries:', error);
            toast.error('Error loading countries');
        }
    };

    const fetchStatesByCountry = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`https://app.bihebari.com.np/api/locations/states/${filterCountry}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setStates(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching states:', error);
            toast.error('Error loading states');
        }
    };

    const fetchCities = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            let url = `https://app.bihebari.com.np/api/admin/cities?page=${currentPage}&limit=${itemsPerPage}`;
            
            if (filterCountry) {
                url += `&country_id=${filterCountry}`;
            }
            
            if (filterState) {
                url += `&state_id=${filterState}`;
            }
            
            if (searchTerm) {
                url += `&search=${encodeURIComponent(searchTerm)}`;
            }
            
            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                setCities(response.data.data);
                setTotalItems(response.data.pagination?.total || response.data.data.length);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            toast.error('Error loading cities');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setSearchTerm(tempSearch);
        setCurrentPage(1);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const clearSearch = () => {
        setTempSearch('');
        setSearchTerm('');
        setCurrentPage(1);
    };

    const handleOpenModal = (city = null) => {
        if (city) {
            setEditingCity(city);
            // Fetch states for the city's country to populate dropdown
            if (city.country_id) {
                fetchStatesForModal(city.country_id);
            }
            setFormData({
                name: city.name,
                state_id: city.state_id,
                sort_order: city.sort_order || 0,
                lang: city.lang || 'en',
                is_default: city.is_default || 0
            });
        } else {
            setEditingCity(null);
            setFormData({
                name: '',
                state_id: '',
                sort_order: 0,
                lang: 'en',
                is_default: 0
            });
            setStates([]);
        }
        setModalOpen(true);
    };

    const fetchStatesForModal = async (countryId) => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`https://app.bihebari.com.np/api/locations/states/${countryId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setStates(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching states for modal:', error);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingCity(null);
        setFormData({
            name: '',
            state_id: '',
            sort_order: 0,
            lang: 'en',
            is_default: 0
        });
        setStates([]);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'state_id') {
            // When state is selected, we can get country info from the selected state
            const selectedState = states.find(s => s.id == value);
            if (selectedState) {
                setFormData({
                    ...formData,
                    [name]: value
                });
            } else {
                setFormData({
                    ...formData,
                    [name]: value
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.state_id) {
            toast.error('Please fill all required fields');
            return;
        }
        
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingCity 
                ? `https://app.bihebari.com.np/api/admin/cities/${editingCity.city_id}`
                : 'https://app.bihebari.com.np/api/admin/cities';
            const method = editingCity ? 'put' : 'post';
            
            await axios[method](url, formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success(editingCity ? 'City updated successfully' : 'City created successfully');
            handleCloseModal();
            fetchCities();
        } catch (error) {
            console.error('Error saving city:', error);
            toast.error(error.response?.data?.message || 'Error saving city');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, cityName) => {
        if (window.confirm(`Are you sure you want to delete "${cityName}"?`)) {
            try {
                const token = localStorage.getItem('adminToken');
                await axios.delete(`https://app.bihebari.com.np/api/admin/cities/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('City deleted successfully');
                fetchCities();
            } catch (error) {
                console.error('Error deleting city:', error);
                toast.error('Error deleting city');
            }
        }
    };

    const getCountryName = (countryId) => {
        const country = countries.find(c => c.id === countryId);
        return country ? country.name : 'Unknown';
    };

    const getStatusBadge = (isActive) => {
        return isActive === 1 
            ? <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Active</span>
            : <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Inactive</span>;
    };

    const getDefaultBadge = (isDefault) => {
        return isDefault === 1 
            ? <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
                <FaStar className="text-xs" /> Default
              </span>
            : null;
    };

    // Calculate pagination
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const goToPage = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Generate page numbers
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxPagesToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        
        if (endPage - startPage + 1 < maxPagesToShow) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }
        return pageNumbers;
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">City Management</h1>
                <button
                    onClick={() => handleOpenModal()}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition"
                >
                    <FaPlus />
                    <span>Add New City</span>
                </button>
            </div>

            {/* Filter and Search Bar */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Country Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <FaGlobe className="inline mr-1" /> Filter by Country
                        </label>
                        <select
                            value={filterCountry}
                            onChange={(e) => {
                                setFilterCountry(e.target.value);
                                setFilterState(''); // Reset state when country changes
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">All Countries</option>
                            {countries.map(country => (
                                <option key={country.id} value={country.id}>
                                    {country.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    {/* State Filter - Depends on Country */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <FaMapMarker className="inline mr-1" /> Filter by State
                        </label>
                        <select
                            value={filterState}
                            onChange={(e) => setFilterState(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            disabled={!filterCountry}
                        >
                            <option value="">All States</option>
                            {states.map(state => (
                                <option key={state.id} value={state.id}>
                                    {state.name}
                                </option>
                            ))}
                        </select>
                        {filterCountry && states.length === 0 && (
                            <p className="text-xs text-gray-500 mt-1">No states found for this country</p>
                        )}
                        {!filterCountry && (
                            <p className="text-xs text-gray-500 mt-1">Please select a country first</p>
                        )}
                    </div>
                    
                    {/* Search City */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <FaCity className="inline mr-1" /> Search City
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={tempSearch}
                                onChange={(e) => setTempSearch(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Search by city name..."
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            />
                            <button
                                onClick={handleSearch}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                            >
                                Search
                            </button>
                            {searchTerm && (
                                <button
                                    onClick={clearSearch}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Active Filters */}
                {(filterCountry || filterState || searchTerm) && (
                    <div className="mt-4 pt-3 border-t flex flex-wrap items-center gap-2">
                        <span className="text-sm text-gray-600">Active filters:</span>
                        {filterCountry && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                Country: {getCountryName(parseInt(filterCountry))}
                                <button onClick={() => {
                                    setFilterCountry('');
                                    setFilterState('');
                                }} className="hover:text-green-900">
                                    <FaTimes size={12} />
                                </button>
                            </span>
                        )}
                        {filterState && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                                State: {states.find(s => s.id === parseInt(filterState))?.name}
                                <button onClick={() => setFilterState('')} className="hover:text-blue-900">
                                    <FaTimes size={12} />
                                </button>
                            </span>
                        )}
                        {searchTerm && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                                Search: {searchTerm}
                                <button onClick={clearSearch} className="hover:text-purple-900">
                                    <FaTimes size={12} />
                                </button>
                            </span>
                        )}
                        <button 
                            onClick={() => {
                                setFilterCountry('');
                                setFilterState('');
                                clearSearch();
                            }} 
                            className="text-red-500 hover:text-red-700 text-sm"
                        >
                            Clear all
                        </button>
                    </div>
                )}
            </div>

            {/* Results Info */}
            <div className="mb-4 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                    Showing {cities.length} of {totalItems} cities
                </p>
                <p className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                </p>
            </div>

            {/* Cities Table */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">City Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sort Order</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Language</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center">
                                        <FaSpinner className="text-4xl text-indigo-600 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : cities.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                                        No cities found
                                    </td>
                                </tr>
                            ) : (
                                cities.map((city) => (
                                    <tr key={city.city_id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900">{city.city_id}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <FaCity className="text-indigo-500" />
                                                <span className="text-sm font-medium text-gray-900">{city.name}</span>
                                                {getDefaultBadge(city.is_default)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{city.state_name}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{city.country_name}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{city.sort_order || 0}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{city.lang?.toUpperCase() || 'EN'}</td>
                                        <td className="px-6 py-4">{getStatusBadge(city.is_active)}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleOpenModal(city)}
                                                    className="text-blue-600 hover:text-blue-800 transition p-1"
                                                    title="Edit City"
                                                >
                                                    <FaEdit size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(city.city_id, city.name)}
                                                    className="text-red-600 hover:text-red-800 transition p-1"
                                                    title="Delete City"
                                                >
                                                    <FaTrash size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-6">
                    <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition flex items-center gap-2"
                    >
                        <FaChevronLeft />
                        Previous
                    </button>
                    
                    <div className="flex space-x-1">
                        {getPageNumbers().map(pageNum => (
                            <button
                                key={pageNum}
                                onClick={() => goToPage(pageNum)}
                                className={`px-3 py-2 rounded-lg transition ${
                                    currentPage === pageNum
                                        ? 'bg-indigo-600 text-white'
                                        : 'border hover:bg-gray-50'
                                }`}
                            >
                                {pageNum}
                            </button>
                        ))}
                    </div>
                    
                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition flex items-center gap-2"
                    >
                        Next
                        <FaChevronRight />
                    </button>
                </div>
            )}

            {/* Add/Edit Modal - Updated to include Country selection first */}
            {modalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h2 className="text-xl font-bold text-gray-800">
                                {editingCity ? 'Edit City' : 'Add New City'}
                            </h2>
                            <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                                <FaTimes />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="space-y-4">
                                {!editingCity && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Country <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            id="country_select"
                                            onChange={(e) => {
                                                const countryId = e.target.value;
                                                if (countryId) {
                                                    fetchStatesForModal(countryId);
                                                } else {
                                                    setStates([]);
                                                }
                                            }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                        >
                                            <option value="">Select Country</option>
                                            {countries.map(country => (
                                                <option key={country.id} value={country.id}>
                                                    {country.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        State <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="state_id"
                                        value={formData.state_id}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                        required
                                        disabled={!editingCity && states.length === 0}
                                    >
                                        <option value="">Select State</option>
                                        {states.map(state => (
                                            <option key={state.id} value={state.id}>
                                                {state.name}
                                            </option>
                                        ))}
                                    </select>
                                    {!editingCity && states.length === 0 && (
                                        <p className="text-xs text-gray-500 mt-1">Please select a country first</p>
                                    )}
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        City Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder="Enter city name"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                        required
                                    />
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Sort Order
                                        </label>
                                        <input
                                            type="number"
                                            name="sort_order"
                                            value={formData.sort_order}
                                            onChange={handleInputChange}
                                            placeholder="0"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Language
                                        </label>
                                        <select
                                            name="lang"
                                            value={formData.lang}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                        >
                                            <option value="en">English</option>
                                            <option value="np">Nepali</option>
                                            <option value="hi">Hindi</option>
                                        </select>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        name="is_default"
                                        id="is_default"
                                        checked={formData.is_default === 1}
                                        onChange={handleInputChange}
                                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    />
                                    <label htmlFor="is_default" className="text-sm font-medium text-gray-700">
                                        Set as default city for this state
                                    </label>
                                </div>
                            </div>
                            
                            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center space-x-2"
                                >
                                    {loading ? <FaSpinner className="animate-spin" /> : <FaSave />}
                                    <span>{editingCity ? 'Update' : 'Save'}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CityManagement;